import React, {
  useCallback, useEffect, useMemo, useState, ReactElement,
} from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, Navigate } from 'react-router';
import Table from '../../components/table/table.component';
import TableBody from '../../components/table-body/table-body.component';
import TableRow from '../../components/table-row/table-row.component';
import TableCell from '../../components/table-cell/table-cell.component';
import LimitedPageContainer from '../../components/limited-page-block/limited-page-container.component';
import ContentHeader from '../../components/content-header/content-header.component';
import Loader from '../../components/loader/loader.component';
import Hint from '../../components/hint/hint.component';
import ConfirmModal from '../../components/confirm-modal/confirm-modal.component';
import CheckAccess from '../../components/check-access/check-access.component';
import ReportNewModal from '../report-list/report-new-modal/report-new-modal.component';
import StatusBar from '../../components/status-bar/status-bar.component';
import RequiredMark from '../../components/required-mark/required-mark.component';
import PageError from '../../components/page-error/page-error.component';
import PullToRefresh from '../../components/pull-to-refresh/pull-to-refresh.component';
import ControlButton from '../../components/control-button/control-button.component';
import SinglePageBlock from '../../components/single-page-block/single-page-block.component';
import Form from '../../components/form/form.component';
import FormContainer from '../../components/form-container/form-container.component';
import FormError from '../../components/form-error/form-error.component';
import FormFieldSelect from '../../components/form-field-select/form-field-select.component';
import FormFieldMultiSelect from '../../components/form-field-multi-select/form-field-multi-select.component';
import FormFieldText from '../../components/form-field-text/form-field-text.component';
import FormFieldCheckbox from '../../components/form-field-checkbox/form-field-checkbox.component';
import FormFieldDate from '../../components/form-field-date/form-field-date.component';
import MenuItemList from '../../components/menu-item-list/menu-item-list.component';
import ControlButtonBlock from '../app/common/control-button-block/control-button-block.component';
import Controls from '../app/controls/controls.component';
import ReportValidator from './report.validator';
import ScreenService from '../../services/screen-service';
import DateService from '../../services/date-service';
import FormatService from '../../services/format-service';
import useScreenSmall from '../../hooks/use-screen-small';
import useFormStructureModified from '../../hooks/use-form-structure-modified';
import useServerFormErrors from '../../hooks/use-server-form-errors';
import {
  getReportData, generateReportData, saveReportData, resetReportFile, resetReportData,
} from './report.action';
import { deleteReport } from '../report-list/report-list.action';
import { getListActivityDealData } from '../app/search-list/search-list.action';
import { resetFormErrors } from '../app/app.action';
import { REPORT_VIEW, REPORT_CREATE, REPORT_EDIT, REPORT_DELETE } from '../../constants/entity/permissions';
import { FORM_ID_REPORT } from '../../constants/system/form-ids';
import { ROUTE_REPORT, ROUTE_REPORT_LIST, ROUTE_NOT_FOUND, ROUTE_PARAM_ID } from '../../constants/system/routes';
import {
  REPORT_TYPE_LIST_DEALS_FILTER, REPORT_TYPE_LIST_PORTFOLIOS_FILTER, REPORT_TYPE_LIST_ACTIVITY_FIELDS_FILTER,
  REPORT_TYPE_LIST_DEAL_FIELDS_FILTER, REPORT_TYPE_LIST_HIDDEN_PORTFOLIOS,
} from '../../constants/entity/report-type-lists';
import { ACCOUNT_USER_TYPE_LIST_ADMIN } from '../../constants/entity/account-user-type-lists';
import { REPORT_TYPE_DEAL_TASK_DUE_DILIGENCE_STATUS } from '../../constants/entity/report-types';
import {
  REPORT_TIMEFRAME_TYPE_CUSTOM, REPORT_TIMEFRAME_TYPE_NO_LIMIT,
} from '../../constants/entity/report-timeframe-types';
import { TIMEOUT_SHORT } from '../../constants/system/timeouts';
import REPORT_FORMAT_PDF from '../../constants/entity/report-formats';
import AssetDefaultImage from '../../static/images/asset-default-image.png';
import FormService from '../../services/form-service';

const Report: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const PORTFOLIO_HIDDEN_LABEL: string = 'Hidden';

  const {
    isDataLoaded,
    isRightDrawerOpen,
    currentUser,
    reportData,
    reportFile,
    portfolioList,
    hiddenPortfolioList,
    dealList,
    dealStatusList,
    activityStatusList,
    activityTypeList,
    reportTimeframeTypeList,
    reportFormatList,
    form: formProps,
    modified: formModifiedFields,
    handleSubmit: submitForm,
    pristine: isFormPristine,
    isSubmitting: isFormSaving,
    error: formError,
    values: formValues,
    hasValidationErrors: withFormErrors,
    formErrors: serverFormErrors,
    mainFormError: mainServerFormError,
  } = props;

  const {
    reset: resetForm,
    change: changeForm,
  } = formProps;

  const routeParams: ReactRouterParams = useParams();
  const navigate: ReactRouterNavigate = useNavigate();
  const dispatch: ReduxDispatch = useDispatch();
  const isSmallScreen: boolean = useScreenSmall();
  const isFormStructureModified: boolean = useFormStructureModified(formModifiedFields, isDataLoaded);

  const formPrefix: string = 'reportData';
  const reportId: number = FormatService.parseInt(routeParams[ROUTE_PARAM_ID]);
  const initialData: JsonObject = reportData && reportData.initialData;
  const initialReportTimeframeType: string = (initialData && initialData.reportTimeframeType)
    || REPORT_TIMEFRAME_TYPE_NO_LIMIT;
  const reportType: string = reportData && reportData.type;
  const isDealRequired: boolean = reportType === REPORT_TYPE_DEAL_TASK_DUE_DILIGENCE_STATUS;
  const isSystemReport: boolean = reportData && reportData.isSystem;
  const isSaveDisabled: boolean = isFormSaving || (isFormPristine && !isFormStructureModified && !isSystemReport);
  const reportName: string | null = reportData && reportData.name ? reportData.name : null;
  const pageTitle: string = `Report${reportName ? `: ${reportName}` : ''}`;
  const withServerErrors: boolean = serverFormErrors && !!Object.keys(serverFormErrors).length;
  const deleteConfirmModalTitle: string = 'Delete Report';
  const deleteConfirmModalText: string = 'Are you sure you want to delete this report?';
  const savePermission: string = reportData && reportData.isSystem ? REPORT_CREATE : REPORT_EDIT;

  const [isOpenMessage, setIsOpenMessage]: [boolean, Function] = useState(false);
  const [statusMessage, setStatusMessage]: [string, Function] = useState('');
  const [selectedDealStatuses, setSelectedDealStatuses]: [DealStatus[] | null, Function] = useState(null);
  const [selectedActivityStatuses, setSelectedActivityStatuses]: [ActivityStatus[] | null, Function] = useState(null);
  const [selectedDeals, setSelectedDeals]: [Deal[] | null, Function] = useState<StateValueDefault>(null);
  const [selectedPortfolios, setSelectedPortfolios]: [Portfolio[] | null, Function] = useState<StateValueDefault>(null);
  const [errorMessage, setErrorMessage]: [string | null, Function] = useState(null);
  const [isNewReportModalOpen, setIsNewReportModalOpen]: [boolean, Function] = useState(false);
  const [isReportCreated, setIsReportCreated]: [boolean, Function] = useState(false);
  const [reportTimeframeTypeValue, setReportTimeframeTypeValue]: [string | null, Function] = useState(null);
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen]: [boolean, Function] = useState(false);
  const [confirmModalErrorMessage, setConfirmModalErrorMessage]: [string | null, Function] = useState(null);

  const reportViewPermissions: string[] = useMemo((): string[] => [REPORT_VIEW], []);
  const commonPortfolioList: Portfolio[] = useMemo((): Portfolio[] => [
    ...(portfolioList || []),
    ...(hiddenPortfolioList ? hiddenPortfolioList.map((portfolioItem: Portfolio): Portfolio => ({
      ...portfolioItem,
      name: `${portfolioItem.name} (${PORTFOLIO_HIDDEN_LABEL})`,
    })) : []),
  ], [portfolioList, hiddenPortfolioList]);

  const isCurrentUserAdmin: boolean = !!currentUser && !!currentUser.accountUserType
    && ACCOUNT_USER_TYPE_LIST_ADMIN.includes(currentUser.accountUserType);
  const isMultiplePortfolioGroupView: boolean = useMemo((): boolean => (
    commonPortfolioList && commonPortfolioList.length ? commonPortfolioList
      .map((portfolioItem: Portfolio): number | null => portfolioItem.group ? portfolioItem.group.id : null)
      .filter((
        portfolioGroupId: number | null, portfolioGroupIndex: number, portfolioItemList: (number | null)[],
      ): boolean => (
        !!portfolioGroupId && portfolioItemList.indexOf(portfolioGroupId) === portfolioGroupIndex
      )).length > 1 : false
  ), [commonPortfolioList]);
  const initialSelectedDeals: Deal[] = useMemo((): Deal[] => (
    initialData && initialData.dealIds && initialData.dealIds.length && dealList && dealList.length
      ? initialData.dealIds.map((dealId: number): Deal => (
        dealList.find((dealItem: Deal): boolean => dealItem.id === dealId)
      )).filter((dealItem: Deal): boolean => !!dealItem) : []
  ), [initialData, dealList]);
  const initialSelectedPortfolios: Portfolio[] = useMemo((): Portfolio[] => (
    initialData && initialData.portfolioIds && initialData.portfolioIds.length
    && commonPortfolioList && commonPortfolioList.length
      ? initialData.portfolioIds.map((portfolioId: number): Portfolio | undefined => (
        (commonPortfolioList || []).find((portfolioItem: Portfolio): boolean => portfolioItem.id === portfolioId)
      )).filter((portfolioItem: Portfolio): boolean => !!portfolioItem) as Portfolio[] : []
  ), [initialData, commonPortfolioList]);
  const initialSelectedDealStatuses: DealStatus[] = useMemo((): DealStatus[] => (
    initialData && initialData.dealStatusIds && initialData.dealStatusIds.length
    && dealStatusList && dealStatusList.length
      ? initialData.dealStatusIds.map((dealStatusId: number): DealStatus => (
        dealStatusList.find((dealStatusItem: DealStatus): boolean => dealStatusItem.id === dealStatusId)
      )).filter((dealStatusItem: DealStatus): boolean => !!dealStatusItem) : []
  ), [initialData, dealStatusList]);
  const initialSelectedActivityStatuses: ActivityStatus[] = useMemo((): ActivityStatus[] => (
    initialData && initialData.activityStatusIds && initialData.activityStatusIds.length
    && activityStatusList && activityStatusList.length
      ? initialData.activityStatusIds.map((activityStatusId: number): ActivityStatus => (
        activityStatusList.find((activityStatusItem: ActivityStatus): boolean => (
          activityStatusItem.id === activityStatusId
        ))
      )).filter((activityStatusItem: ActivityStatus): boolean => !!activityStatusItem) : []
  ), [initialData, activityStatusList]);

  const menuItemReportTimeframeTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: reportTimeframeTypeList,
  }), [reportTimeframeTypeList]);

  const menuItemPortfolioList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    isGrouped: isMultiplePortfolioGroupView,
    groupKey: 'group',
    groupItemLabelKey: 'name',
    optionItemLabelKey: 'name',
    optionList: commonPortfolioList,
    selectedItemList: selectedPortfolios,
  }), [commonPortfolioList, selectedPortfolios, isMultiplePortfolioGroupView]);

  const menuItemDealList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'name',
    optionList: dealList,
    selectedItemList: selectedDeals,
    optionItemImageKey: 'mainPhoto',
    optionItemImageThumbnailKey: 'mainPhotoThumbnail',
    defaultImage: AssetDefaultImage,
  }), [dealList, selectedDeals]);

  const menuItemDealStatusList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionList: dealStatusList,
    selectedItemList: selectedDealStatuses,
  }), [dealStatusList, selectedDealStatuses]);

  const menuItemActivityStatusList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionList: activityStatusList,
    selectedItemList: selectedActivityStatuses,
  }), [activityStatusList, selectedActivityStatuses]);

  const menuItemActivityTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: activityTypeList,
  }), [activityTypeList]);

  const menuItemReportFormatList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: reportFormatList,
  }), [reportFormatList]);

  const onActivityDealSearch: Function = useCallback((query: string): void => {
    dispatch(getListActivityDealData({
      query,
    }));
  }, []);

  const onActivityDealSearchClose: Function = useCallback((): void => {
    dispatch(getListActivityDealData({
      query: null,
    }));
  }, []);

  const onCancelForm: Function = useCallback((): void => {
    FormService.forceResetForm(resetForm, formProps);
  }, []);

  const dealsChange: Function = useCallback((dealValues: Deal[]): void => {
    changeForm(`${formPrefix}.deals`, dealValues);
    setSelectedDeals(dealValues);
  }, []);

  const portfoliosChange: Function = useCallback((portfolioValues: Portfolio[]): void => {
    changeForm(`${formPrefix}.portfolios`, portfolioValues);
    changeForm(`${formPrefix}.includeHiddenPortfolios`, false);
    setSelectedPortfolios(portfolioValues);
  }, []);

  const dealStatusesChange: Function = useCallback((dealStatusValues: DealStatus[]): void => {
    changeForm(`${formPrefix}.dealStatuses`, dealStatusValues);
    setSelectedDealStatuses(dealStatusValues);
  }, []);

  const activityStatusesChange: Function = useCallback((activityStatusValues: ActivityStatus[]): void => {
    changeForm(`${formPrefix}.activityStatuses`, activityStatusValues);
    setSelectedActivityStatuses(activityStatusValues);
  }, []);

  const onReportTimeframeTypeChange: Function = useCallback((reportTimeframeTypeName: string): void => {
    setReportTimeframeTypeValue(reportTimeframeTypeName);
  }, []);

  const openDeleteConfirmModal: Function = useCallback((): void => {
    setIsDeleteConfirmModalOpen(true);
  }, []);

  const closeDeleteConfirmModal: Function = useCallback((): void => {
    setIsDeleteConfirmModalOpen(false);
    setConfirmModalErrorMessage(null);
  }, []);

  const onDeleteClick: Function = useCallback((): void => {
    openDeleteConfirmModal();
  }, []);

  const onDeleteConfirm: Function = useCallback((): Promise<ActionCallback> | null => {
    if (!reportId) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(deleteReport({
        reportId,
      }, (response: ActionResponse): void => {
        setConfirmModalErrorMessage(null);
        setIsDeleteConfirmModalOpen(false);

        setStatusMessage('Deleted successfully');
        setIsOpenMessage(true);

        setTimeout((): void => {
          navigate({
            pathname: ROUTE_REPORT_LIST,
          });
        }, TIMEOUT_SHORT);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setConfirmModalErrorMessage(exception.message);
        }
      }));
    });
  }, [reportId]);

  const sendForm: Function = useCallback((values: FormValues, isSaveForm?: boolean): Promise<ActionCallback> | null => {
    if (!values.reportData || !values.reportData.reportFormat) {
      return null;
    }

    const {
      reportFormat,
      reportTimeframeType,
      portfolios,
      deals,
      dealStatuses,
      activityStatuses,
      activityType,
      includeHiddenPortfolios,
      isAutoGenerated,
      fromDate,
      toDate,
      name,
    } = values.reportData;

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch((isSaveForm ? saveReportData : generateReportData)({
        reportForm: {
          reportId,
          reportFormat,
          portfolioIds: portfolios && portfolios.length
            ? portfolios.map((portfolioItem: Portfolio): number => portfolioItem.id) : [],
          dealIds: deals && deals.length ? deals.map((dealItem: Deal): number => dealItem.id) : [],
          dealStatusIds: dealStatuses && dealStatuses.length
            ? dealStatuses.map((dealStatusItem: DealStatus): number => dealStatusItem.id) : [],
          activityStatusIds: activityStatuses && activityStatuses.length
            ? activityStatuses.map((activityStatusItem: ActivityStatus): number => activityStatusItem.id) : [],
          activityType,
          includeHiddenPortfolios,
          isAutoGenerated,
          reportTimeframeType,
          fromDate: reportTimeframeType === REPORT_TIMEFRAME_TYPE_CUSTOM ? fromDate : null,
          toDate: reportTimeframeType === REPORT_TIMEFRAME_TYPE_CUSTOM ? toDate : null,
          name,
        },
        reportId: isSaveForm ? reportId : null,
      }, (response: ActionResponse): void => {
        setIsNewReportModalOpen(false);

        if (reportData && reportData.isSystem) {
          setIsReportCreated(true);
        }

        if (isSaveForm) {
          onCancelForm();
        }

        setStatusMessage(isSaveForm ? 'Report template was saved successfully' : 'Report generated successfully');
        setIsOpenMessage(true);

        isSaveForm = false;

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          submitForm();
        }
      }));
    });
  }, [reportId, reportData]);

  const onSubmitForm: Function = useCallback((isSaveFormMode?: boolean): void => {
    submitForm();

    if (!withFormErrors) {
      sendForm(formValues, isSaveFormMode);
    } else {
      FormService.focusOnFirstFormError();
    }
  }, [reportId, reportData, withFormErrors, formValues]);

  const onSaveReport: MouseEventHandlerFunction = useCallback((): void => {
    if (reportData && reportData.isSystem) {
      setIsNewReportModalOpen(true);
    } else {
      onSubmitForm(true);
    }
  }, [reportId, reportData, withFormErrors, formValues]);

  const onReportCreate: Function = useCallback((): void => {
    return onSubmitForm(true);
  }, [reportId, reportData, withFormErrors, formValues]);

  const closeMessage: Function = useCallback((): void => {
    setIsOpenMessage(false);
    setStatusMessage('');
  }, []);

  const closeNewReportModal: Function = useCallback((): void => {
    setIsNewReportModalOpen(false);
  }, []);

  const onRefresh: Function = useCallback((): void => {
    if (reportId) {
      dispatch(getReportData({
        reportId,
      }, undefined, (exception: ServerError): void => {
        if (exception) {
          setErrorMessage(exception.message);
        }
      }));
    }
  }, [reportId]);

  const onResetReportData: Function = useCallback((): void => {
    dispatch(resetReportData());
  }, []);

  const onFormChange: Function = useCallback((event: React.SyntheticEvent): void => {
    const fieldName: string = event.target && (event.target as HTMLInputElement).name;

    if (withServerErrors && fieldName) {
      dispatch(resetFormErrors());
      FormService.resetFormServerErrors(formProps, fieldName);
      submitForm();
    }
  }, [withServerErrors, formProps]);

  useEffect((): ReactUseEffectCleanUp => {
    if (isDataLoaded) {
      onResetReportData();
    }

    if (reportId) {
      dispatch(getReportData({
        reportId,
      }, undefined, (exception: ServerError): void => {
        if (exception) {
          setErrorMessage(exception.message);
        }
      }));
    }

    return (): void => {
      onResetReportData();
    };
  }, [reportId]);

  useEffect((): void => {
    if (reportFile && reportFile.link) {
      ScreenService.downloadFile(reportFile.link);
      dispatch(resetReportFile());
    }
  }, [reportFile]);

  useEffect((): void => {
    if (isReportCreated && reportData.id !== reportId) {
      setIsReportCreated(false);

      navigate({
        pathname: `${ROUTE_REPORT}/${reportData.id}`,
      });
    }
  }, [isReportCreated]);

  useEffect((): void => {
    if (reportData) {
      setReportTimeframeTypeValue(initialReportTimeframeType);
      setSelectedDeals(initialSelectedDeals);
      setSelectedPortfolios(initialSelectedPortfolios);
      setSelectedDealStatuses(initialSelectedDealStatuses);
      setSelectedActivityStatuses(initialSelectedActivityStatuses);
    }
  }, [reportData]);

  useServerFormErrors(serverFormErrors, (): void => {
    submitForm();
  });

  if (errorMessage) {
    return (
      <PageError message={errorMessage} />
    );
  }

  if (!reportId) {
    return <Navigate to={ROUTE_NOT_FOUND} />;
  }

  if (!isDataLoaded) {
    return <Loader isPageLoader />;
  }

  return (
    <CheckAccess permissions={reportViewPermissions}>
      <Form id={FORM_ID_REPORT} onChange={onFormChange}>
        <PullToRefresh onRefresh={onRefresh}>
          <ContentHeader title={pageTitle} />
          <Controls
            withNoEditMode
            withDelete={!isSystemReport}
            isHidden={isSystemReport}
            deletePermission={REPORT_DELETE}
            onDeleteClick={onDeleteClick}
          />
          <LimitedPageContainer isActive>
            <SinglePageBlock isFullWidth withNoTabsOffset>
              <Table>
                <TableBody isPlain withNoBorders withTwoColumns withHeaderColumn>
                  {
                    !reportData.isSystem && (
                      <TableRow>
                        <TableCell>
                          Report Name
                          <RequiredMark />
                        </TableCell>
                        <TableCell>
                          <FormFieldText isBordered name={`${formPrefix}.name`} />
                        </TableCell>
                      </TableRow>
                    )
                  }
                  <TableRow>
                    <TableCell>
                      Timeframe
                      <RequiredMark />
                    </TableCell>
                    <TableCell>
                      <FormFieldSelect
                        isBordered
                        name={`${formPrefix}.reportTimeframeType`}
                        optionsTotal={reportTimeframeTypeList && reportTimeframeTypeList.length}
                        onChange={((reportTimeframeTypeName: string): void => {
                          onReportTimeframeTypeChange(reportTimeframeTypeName);
                        })}
                      >
                        {menuItemReportTimeframeTypeList}
                      </FormFieldSelect>
                    </TableCell>
                  </TableRow>
                  {
                    reportTimeframeTypeValue === REPORT_TIMEFRAME_TYPE_CUSTOM && (
                      <TableRow>
                        <TableCell>
                          From Date
                        </TableCell>
                        <TableCell>
                          <FormFieldDate
                            isBordered
                            withNoFutureSelection
                            name={`${formPrefix}.fromDate`}
                          />
                        </TableCell>
                      </TableRow>
                    )
                  }
                  {
                    reportTimeframeTypeValue === REPORT_TIMEFRAME_TYPE_CUSTOM && (
                      <TableRow>
                        <TableCell>
                          To Date
                        </TableCell>
                        <TableCell>
                          <FormFieldDate
                            isBordered
                            withNoFutureSelection
                            name={`${formPrefix}.toDate`}
                          />
                        </TableCell>
                      </TableRow>
                    )
                  }
                  {
                    REPORT_TYPE_LIST_PORTFOLIOS_FILTER.includes(reportType)
                    && ((selectedDeals && !selectedDeals.length) || !selectedDeals) && (
                      <TableRow>
                        <TableCell>
                          Portfolios
                        </TableCell>
                        <TableCell>
                          <FormFieldMultiSelect
                            isBordered
                            isDisabled={commonPortfolioList && !commonPortfolioList.length}
                            blankElementLabel="All"
                            name={`${formPrefix}.portfolios`}
                            optionList={commonPortfolioList}
                            onValuesChange={portfoliosChange}
                          >
                            {menuItemPortfolioList}
                          </FormFieldMultiSelect>
                        </TableCell>
                      </TableRow>
                    )
                  }
                  {
                    REPORT_TYPE_LIST_DEALS_FILTER.includes(reportType)
                    && ((selectedPortfolios && !selectedPortfolios.length) || !selectedPortfolios) && (
                      <TableRow>
                        <TableCell>
                          Deals
                          {
                            isDealRequired && (
                              <RequiredMark />
                            )
                          }
                        </TableCell>
                        <TableCell>
                          <FormFieldMultiSelect
                            isBordered
                            withImage
                            withSearch
                            isDisabled={dealList && !dealList.length}
                            blankElementLabel={isDealRequired ? 'None' : 'All'}
                            imageThumbnailKey="mainPhotoThumbnail"
                            imageKey="mainPhoto"
                            name={`${formPrefix}.deals`}
                            defaultImage={AssetDefaultImage}
                            optionList={dealList}
                            onSearch={onActivityDealSearch}
                            onClose={onActivityDealSearchClose}
                            onValuesChange={dealsChange}
                          >
                            {menuItemDealList}
                          </FormFieldMultiSelect>
                        </TableCell>
                      </TableRow>
                    )
                  }
                  {
                    REPORT_TYPE_LIST_DEAL_FIELDS_FILTER.includes(reportType) && (
                      <TableRow>
                        <TableCell>
                          Deal Statuses
                        </TableCell>
                        <TableCell>
                          <FormFieldMultiSelect
                            isBordered
                            isDisabled={dealStatusList && !dealStatusList.length}
                            blankElementLabel="All"
                            name={`${formPrefix}.dealStatuses`}
                            optionList={dealStatusList}
                            onValuesChange={dealStatusesChange}
                          >
                            {menuItemDealStatusList}
                          </FormFieldMultiSelect>
                        </TableCell>
                      </TableRow>
                    )
                  }
                  {
                    REPORT_TYPE_LIST_ACTIVITY_FIELDS_FILTER.includes(reportType) && (
                      <TableRow>
                        <TableCell>
                          Activity Statuses
                        </TableCell>
                        <TableCell>
                          <FormFieldMultiSelect
                            isBordered
                            isDisabled={activityStatusList && !activityStatusList.length}
                            blankElementLabel="All"
                            name={`${formPrefix}.activityStatuses`}
                            optionList={activityStatusList}
                            onValuesChange={activityStatusesChange}
                          >
                            {menuItemActivityStatusList}
                          </FormFieldMultiSelect>
                        </TableCell>
                      </TableRow>
                    )
                  }
                  {
                    REPORT_TYPE_LIST_ACTIVITY_FIELDS_FILTER.includes(reportType) && (
                      <TableRow>
                        <TableCell>
                          Activity Type
                        </TableCell>
                        <TableCell>
                          <FormFieldSelect
                            isBordered
                            withBlankElement
                            blankElementLabel="All"
                            name={`${formPrefix}.activityType`}
                            optionsTotal={activityTypeList && activityTypeList.length}
                          >
                            {menuItemActivityTypeList}
                          </FormFieldSelect>
                        </TableCell>
                      </TableRow>
                    )
                  }
                  {
                    REPORT_TYPE_LIST_HIDDEN_PORTFOLIOS.includes(reportType)
                    && ((selectedPortfolios && !selectedPortfolios.length) || !selectedPortfolios) && (
                      <TableRow>
                        <TableCell>
                          Include Hidden Portfolio
                        </TableCell>
                        <TableCell>
                          <FormFieldCheckbox name={`${formPrefix}.includeHiddenPortfolios`} />
                        </TableCell>
                      </TableRow>
                    )
                  }
                  <TableRow>
                    <TableCell>
                      Format
                      <RequiredMark />
                    </TableCell>
                    <TableCell>
                      <FormFieldSelect
                        isBordered
                        name={`${formPrefix}.reportFormat`}
                        optionsTotal={reportFormatList && reportFormatList.length}
                      >
                        {menuItemReportFormatList}
                      </FormFieldSelect>
                    </TableCell>
                  </TableRow>
                  {
                    isCurrentUserAdmin && !reportData.isSystem && (
                      <TableRow>
                        <TableCell>
                          Enable Auto-Generation
                          <Hint
                            title={'This flag allows to automatically generate current report with all its parameters '
                              + 'applied. Frequency: 1 report per day.'}
                          />
                        </TableCell>
                        <TableCell>
                          <FormFieldCheckbox name={`${formPrefix}.isAutoGenerated`} />
                        </TableCell>
                      </TableRow>
                    )
                  }
                </TableBody>
              </Table>
            </SinglePageBlock>
          </LimitedPageContainer>
          <div>
            <ControlButtonBlock
              isSaveDefault
              withNoCancel
              withSaveButton
              withLoader={isFormSaving && !isSmallScreen}
              isSaveDisabled={isSaveDisabled}
              savePermission={savePermission}
              labelSave="Save Template"
              onSubmitForm={onSaveReport}
              extraButtonComponent={(
                <ControlButton
                  isActive
                  isDisabled={isFormSaving}
                  onClick={(): void => {
                    onSubmitForm();
                  }}
                >
                  Generate
                </ControlButton>
              )}
            />
            {
              (!!formError || !!mainServerFormError) && !isFormSaving && (
                <FormError
                  withShowError
                  withLeftOffset={isRightDrawerOpen}
                  error={formError || mainServerFormError}
                />
              )
            }
          </div>
        </PullToRefresh>
        <ReportNewModal
          isOpen={isNewReportModalOpen}
          onCreate={onReportCreate}
          onCloseModal={closeNewReportModal}
        />
        <ConfirmModal
          isOpen={isDeleteConfirmModalOpen}
          title={deleteConfirmModalTitle}
          text={deleteConfirmModalText}
          errorMessage={confirmModalErrorMessage}
          onConfirm={onDeleteConfirm}
          onCloseModal={closeDeleteConfirmModal}
        />
        <StatusBar isOpen={isOpenMessage} message={statusMessage} onClose={closeMessage} />
      </Form>
    </CheckAccess>
  );
});

const initialValues: FormValues = {};
const mapState: ReduxMapStateCallback = (reducers: Reducers): ReduxMapState => {
  const {
    app,
    report,
    rightDrawer,
  } = reducers;

  const reportData: Report = report.reportData && report.reportData.reportData;
  const portfolioList: Portfolio[] = report.reportData && report.reportData.portfolioList;
  const hiddenPortfolioList: Portfolio[] = report.reportData && report.reportData.hiddenPortfolioList;
  const commonPortfolioList: Portfolio[] = [...(portfolioList || []), ...(hiddenPortfolioList || [])];
  const dealList: Deal[] = report.reportData && report.reportData.dealList;
  const dealStatusList: DealStatus[] = report.reportData && report.reportData.dealStatusList;
  const activityStatusList: ActivityStatus[] = report.reportData && report.reportData.activityStatusList;
  const reportInitialData: JsonObject = reportData && reportData.initialData;
  const reportFormatList: ReportFormat[] = report.reportData && report.reportData.reportFormatList;
  const reportFormatDefault: string | null = (reportFormatList && !!reportFormatList.length && (
    reportFormatList.find((reportFormat: ReportFormat): boolean => reportFormat.name === REPORT_FORMAT_PDF)
      ? REPORT_FORMAT_PDF : reportFormatList[0].name
  )) || null;

  if (reportData) {
    initialValues.reportData = {
      name: reportData.name,
      type: reportData.type,
      fromDate: (reportInitialData && reportInitialData.fromDate
          && FormatService.formatParsedDate(reportInitialData.fromDate))
        || DateService.parseDate(DateService.getDateYearAgo()),
      toDate: (reportInitialData && reportInitialData.toDate
        && FormatService.formatParsedDate(reportInitialData.toDate)) || DateService.parseDate(DateService.getDateNow()),
      reportFormat: (reportInitialData && reportInitialData.reportFormat) || reportFormatDefault,
      portfolios: (
        reportInitialData && reportInitialData.portfolioIds && commonPortfolioList && commonPortfolioList.length
          ? reportInitialData.portfolioIds.map((portfolioId: number): Portfolio | undefined => (
            commonPortfolioList.find((portfolioItem: Portfolio | undefined): boolean => (
              !!portfolioItem && portfolioItem.id === portfolioId),
            )),
          ).filter((portfolioItem: Portfolio): boolean => !!portfolioItem) : []
      ),
      deals: reportInitialData && reportInitialData.dealIds && dealList
        ? reportInitialData.dealIds.map((dealId: number): Deal | undefined => (
          dealList.find((dealItem: Deal): boolean => !!dealItem && dealItem.id === dealId)
        )).filter((dealItem: Deal | undefined): boolean => !!dealItem) : [],
      dealStatuses: reportInitialData && reportInitialData.dealStatusIds && dealStatusList
        ? reportInitialData.dealStatusIds.map((dealStatusId: number): DealStatus | undefined => (
          dealStatusList.find((dealStatusItem: DealStatus): boolean => (
            !!dealStatusItem && dealStatusItem.id === dealStatusId
          ))
        )).filter((dealStatusItem: DealStatus | undefined): boolean => !!dealStatusItem) : [],
      activityStatuses: reportInitialData && reportInitialData.activityStatusIds && activityStatusList
        ? reportInitialData.activityStatusIds.map((activityStatusId: number): ActivityStatus | undefined => (
          activityStatusList.find((activityStatusItem: ActivityStatus): boolean => (
            !!activityStatusItem && activityStatusItem.id === activityStatusId
          ))
        )).filter((activityStatusItem: ActivityStatus | undefined): boolean => !!activityStatusItem) : [],
      activityType: (reportInitialData && reportInitialData.activityType) || null,
      reportTimeframeType: (reportInitialData && reportInitialData.reportTimeframeType)
        || REPORT_TIMEFRAME_TYPE_NO_LIMIT,
      includeHiddenPortfolios: (reportInitialData && reportInitialData.includeHiddenPortfolios) || false,
      isAutoGenerated: reportData.isAutoGenerated,
    };
  }

  return {
    isDataLoaded: !!report.reportData,
    isRightDrawerOpen: rightDrawer.isDrawerOpen,
    reportData,
    portfolioList,
    hiddenPortfolioList: report.reportData && report.reportData.hiddenPortfolioList,
    dealList,
    dealStatusList,
    activityStatusList,
    activityTypeList: report.reportData && report.reportData.activityTypeList,
    reportTimeframeTypeList: report.reportData && report.reportData.reportTimeframeTypeList,
    reportFormatList,
    reportFile: report.reportFile,
    currentUser: app.baseData && app.baseData.currentUser,
  };
};

const ReportForm: ReactComponent = (props: ReactComponentProps): ReactElement => {
  const formErrors: ValidationErrors = useSelector(({ report }: Reducers): ValidationErrorsMap => (
    report.formErrors
  ));
  const isFormSaving: ValidationErrors = useSelector(({ report }: Reducers): ValidationErrorsMap => (
    report.isFormSaving
  ));

  return (
    <FormContainer
      formId={FORM_ID_REPORT}
      component={Report}
      validator={ReportValidator}
      initialValues={initialValues}
      formErrors={formErrors}
      isSubmitting={isFormSaving}
      {...props}
    />
  );
};

export default connect(mapState)(ReportForm);
